import { log } from '@opengov/node-service/logging';
import _ from 'lodash';
import { v1 as uuidv1 } from 'uuid';
import { IReplacements, IStoriesFolder } from '../../types';
import { TAG_DIV } from '../constants';
import { IStory, IStoryContent } from '@opengov/stories-shared/types';
import {
  COPY_PLAN_TO_PUBLICATION,
  STATE_DRAFT,
  STATE_INTERNAL_PUBLISHED,
  STATE_PUBLISHED
} from '@opengov/stories-shared/constants';
import { ISelectBadgeProps } from '@opengov/component-library/capital/Badge/Badge';
import { findPublicationIdBySectionId } from '../utils';

export function copyStoryContentHTML(content: { content: string; type: string }): string {
  let copiedHTML = content.content;
  try {
    if (content.type === 'body_content' && copiedHTML.includes('OpenGovSDK.embed')) {
      const [, ...stringsWithIds] = copiedHTML.split('data-selector="#embed-');
      stringsWithIds.forEach((stringWithId) => {
        const originalEmbedSelectorId = stringWithId.split('"')[0];
        copiedHTML = _.replace(copiedHTML, new RegExp(originalEmbedSelectorId, 'g'), uuidv1());
      });
    }
  } catch (err) {
    log.error(err);
  }
  return copiedHTML;
}

export function getFileSizeInBytes(fileSize: number): number {
  return fileSize * 1000 * 1000; //size in decimals
}

export type IGetS3SignUrlParams = {
  entitySubdomain: string;
  fileName: string;
  fileType: string;
};

// Query values must be percent-encoded. Filenames routinely contain characters
// that are unsafe in a query string — most notably "%" (and spaces). Interpolating
// them raw yields a malformed query (e.g. "1% transpo.png" produces an invalid "%"
// escape) that the sign-s3 endpoint cannot parse, so the upload fails silently.
export const getS3SignUrl = ({ entitySubdomain, fileName, fileType }: IGetS3SignUrlParams): string =>
  `/api/stories/v1/stories/s3/sign-s3?file-name=${encodeURIComponent(fileName)}&file-type=${encodeURIComponent(
    fileType
  )}&entity=${encodeURIComponent(entitySubdomain)}`;

export const mediaDivTag = `
  <div
    id="embed-{uniqueId}"
    {privacyTag}
    data-update-setting="{updateSetting}"
    data-embed-id="{embedId}"
    data-embed-version='{version}'
    data-embed-disable-click="{disableClick}"
  />
`;

export const mediaDivV3Tag = `
  <div
    id="{uniqueId}"
    {privacyTag}
    data-update-setting="{updateSetting}"
    data-embed-disable-click="{disableClick}"
    data-embed-show-report-link="{showReportLink}"
    data-embed-display-account-codes="{displayAccountCodes}"
    data-embed-title="{embedTitle}"
    aria-label="{embedTitle}"
    aria-labelledby="{embedTitle}"
    data-embed-id="{embedId}"
  />
`;

export const mediaScriptTag = `
  <script 
    type="text/javascript"
    data-embed-id="{embedId}"
    data-embed-version="{version}"
    data-selector="#embed-{uniqueId}"
  >
    OpenGovSDK.embed({
      baseUrl: '{operateUrl}',
      embed: {
        id: '{embedId}',
        type: 'dashboardTile',
        version: '{version}'
      },
      targetSelector: '#embed-{uniqueId}',
      disableClick: {disableClick}
    });
  </script>
`;

export const mediaScriptV3Tag = `
  <script type="text/javascript" data-embed-id="{embedId}" data-selector="#{divId}">
    OpenGovSDK.embed({
      version: '{version}',
      baseUrl: '{operateUrl}',
      embed: {
        id: '{embedId}',
        type: 'dashboardTile',
      },
      targetSelector: '#{divId}',
      disableClick: {disableClick},
      showReportLink: {showReportLink},
      displayAccountCodes: {displayAccountCodes},
      title: '{embedTitle}'
    });
  </script>
`;

export function applyTemplateReplacement(template: string, replacements: IReplacements): string {
  const string = template.replace(/{\w+}/g, (placeholderWithDelimiters) => {
    const placeholderWithoutDelimiters = placeholderWithDelimiters.substring(1, placeholderWithDelimiters.length - 1);
    const stringReplacement = replacements[placeholderWithoutDelimiters] as string | boolean | null | undefined;
    // String.replace stringifies whatever it gets back, so a missing value would silently bake the
    // literal text "undefined" into the story content — most damagingly as an embed id.
    if (stringReplacement == null) {
      throw new Error(`Missing replacement for template placeholder: ${placeholderWithoutDelimiters}`);
    }
    return String(stringReplacement);
  });
  return string;
}

type GetMediaIndexByEmbedIdReturn = void | {
  mediaindex: number;
  content: IStoryContent;
};

export function getMediaIndexByEmbedId(embedId: string, storyContents: IStoryContent[]): GetMediaIndexByEmbedIdReturn {
  let mediaIndexUpdate = 0;
  const embedUpdateContent = storyContents.filter((z) => z.contentHtml.indexOf(embedId) !== -1)[0];
  if (!embedUpdateContent) {
    return;
  }
  const fragment = document.createRange().createContextualFragment(embedUpdateContent.content);
  const mediaBlocks = fragment.querySelectorAll('.has-media');
  mediaBlocks.forEach((mediaBlock) => {
    for (let i = 0; i < mediaBlock.children.length; i++) {
      if (mediaBlock.children[i].tagName === TAG_DIV && mediaBlock.children[i].hasAttribute('data-embed-id')) {
        const mediaBlockEmbedId = mediaBlock.children[i].getAttribute('data-embed-id');
        if (embedId === mediaBlockEmbedId) {
          mediaIndexUpdate = +mediaBlock.getAttribute('data-media-index') - 1;
          break;
        }
      }
    }
  });
  return { mediaindex: mediaIndexUpdate, content: embedUpdateContent };
}

export function extractMessage(error: Error, errorMessage: string): string {
  let errors;
  try {
    errors = JSON.parse(JSON.parse(error.message).message).errors;
  } catch (e) {
    errors = [];
  }
  if (errors.length) {
    errorMessage = `Error Code [${errors[0].code}]: ${errors[0].message}.`;
  }
  return errorMessage;
}
export const getStoryStatusBadgeParams = (story: IStory): ISelectBadgeProps | null => {
  switch (story.state) {
    case STATE_DRAFT:
      return { text: 'Draft', variant: 'neutral' };
    case STATE_INTERNAL_PUBLISHED:
      return { text: 'Organization', variant: 'bright' };
    case STATE_PUBLISHED:
      return { text: 'Public', variant: 'positive' };
    default:
      return null;
  }
};

export const getStoryStatusChipParams = (story: IStory) => {
  switch (story.state) {
    case STATE_DRAFT:
      return { text: 'Draft', variant: 'filled', color: 'default' } as const;
    case STATE_INTERNAL_PUBLISHED:
      return { text: 'Organization', variant: 'filled', color: 'info' } as const;
    case STATE_PUBLISHED:
      return { text: 'Public', variant: 'filled', color: 'success' } as const;
    default:
      return null;
  }
};

export function getPublicationName(
  copyPlan: string,
  folderId: string,
  publicationFoldersTree: IStoriesFolder[]
): string {
  if (folderId && copyPlan === COPY_PLAN_TO_PUBLICATION) {
    const pubIdBySectionId = findPublicationIdBySectionId(publicationFoldersTree, folderId);
    const publication = publicationFoldersTree.find((folder) => folder.id === pubIdBySectionId);
    return publication?.displayName || '';
  }
  return '';
}
